import type { CargoKind, SeedOrderLine, SeedType } from './types';

/**
 * Sim → UI event bus. Plot-coord events use (col, row); placement uses (c, r).
 * UI/render subscribe; the sim itself never depends on UI.
 */
export type SimEvent =
  | { type: 'harvested'; col: number; row: number; cropType: SeedType;
      toGranary: boolean; granaryC?: number; granaryR?: number }
  | { type: 'planted'; col: number; row: number }
  | { type: 'fieldPlaced'; col: number; row: number }
  | { type: 'fieldDried'; col: number; row: number }
  | { type: 'chopped'; col: number; row: number;
      /** Resource yield from this chop so FlyToHud can fly the right glyph.
       *  mushrooms/berries cover the "salvage" case: clearing a stump that
       *  hosts a fungus colony, or a bush that's bearing ripe fruit. */
      wood?: number; stone?: number; acorns?: number;
      mushrooms?: number; berries?: number }
  | { type: 'foraged'; col: number; row: number; kind: 'mushroom' | 'berry'; amount: number }
  | { type: 'mushroomGrew'; col: number; row: number; stage: number }
  | { type: 'berryGrew'; col: number; row: number; stage: number }
  | { type: 'berryPlanted'; col: number; row: number }
  | { type: 'acornPlanted'; col: number; row: number }
  | { type: 'saplingMatured'; col: number; row: number }
  | { type: 'treeSaplingMatured'; col: number; row: number }
  | { type: 'plotTileRemoved'; col: number; row: number }
  | { type: 'watered'; col: number; row: number }
  | { type: 'buildingPlaced'; col: number; row: number; bld: string }
  | { type: 'constructionAdvanced'; col: number; row: number; bld: string; stage: number; total: number }
  | { type: 'constructionComplete'; col: number; row: number; bld: string }
  /** Player just deposited materials toward a construction crate.
   *  FlyToHud animates per-kind from HUD chip → building tile. */
  | { type: 'materialsDeposited'; col: number; row: number;
      kind: 'money' | 'wood' | 'wheat' | 'stone'; qty: number }
  /** Tile reached the 60% threshold for till/water/seed and the
   *  remaining sub-cells were auto-filled for free. UI uses this to
   *  flash the progress bar to 100% and rebuild the affected sub-cell
   *  meshes in one shot. */
  | { type: 'tileAutoCompleted'; col: number; row: number;
      mode: 'till' | 'water' | 'seed' }
  /** A whole adopted plot just crossed its 60% threshold for the given
   *  mode — every plot tile was finished in one shot. HUD uses this to
   *  flash a celebratory burst above the plot. */
  | { type: 'plotAutoCompleted'; plotIdx: number; mode: 'till' | 'water' | 'seed' }
  | { type: 'sold'; coins: number; items: number }
  | { type: 'millStarted'; col: number; row: number }
  | { type: 'millFinished'; col: number; row: number; kind: 'wheat' | 'corn'; flour: number }
  | { type: 'leveledUp'; level: number }
  | { type: 'inspect'; kind: 'tile' | 'storage' | 'mill' | 'mailbox' | 'truck' | 'toolrack' | 'seeds'; col: number; row: number }
  | { type: 'truckArrived' }
  | { type: 'truckLoaded'; kind: CargoKind; qty: number }
  | { type: 'truckDeparted'; coins: number }
  | { type: 'truckReturned'; coins: number }
  /** Truck just reached the city — cargo is unloaded here. Diorama
   *  listens to flash the bed before the crates disappear. */
  | { type: 'truckUnloadedAtCity'; items: number; coins: number }
  | { type: 'platformChanged' }
  /** Moveable world prop (toolrack / seed bag / storage shelf) just landed
   *  at a new tile. Diorama listens to update the mesh position. */
  | { type: 'propMoved'; kind: 'storage' | 'seedbag' | 'toolrack'; col: number; row: number }
  /** Drag-select for a new plot — emitted on selection state changes so
   *  the diorama can refresh its tile-highlight overlay. */
  | { type: 'plotSelectChanged' }
  /** Zones manager opened or closed. UI swaps the sidebar to the plot
   *  cards view; tile clicks route through Sim's zones-mode handlers
   *  instead of the world tool. */
  | { type: 'zonesModeChanged' }
  /** Plot list mutated outside the create/enter/exit flow — rename or
   *  delete via the zones manager. Cards list redraws, fences rebuild.
   *  `releasedTiles` lists tiles that just dropped out of a plot back
   *  to free state (delete or tile-toggle); the diorama rebuilds those
   *  tile groups so their native grass tufts return. */
  | { type: 'plotsChanged'; releasedTiles?: { c: number; r: number }[] }
  /** Selection was confirmed and plot mode is now active. Diorama dives
   *  the camera onto the plot bounding box and dims everything else. */
  | { type: 'plotEntered' }
  /** Plot mode exited — return camera to overview. */
  | { type: 'plotExited' }
  /** A weed tuft was just yanked. Index into activePlot.weeds at time
   *  of pluck — Diorama spawns particles + removes the mesh. */
  | { type: 'weedPlucked'; col: number; row: number; subC: number; subR: number }
  /** A new weed just regenerated somewhere on the field — diorama
   *  rebuilds the weed InstancedMesh so the fresh tuft is visible. */
  | { type: 'weedSpawned'; col: number; row: number; subC: number; subR: number }
  /** A sub-cell was just hoed — Diorama paints a furrow voxel there.
   *  `stage` is the new stage (1 scratch, 2 trench, 3 full furrow). */
  | { type: 'subCellTilled'; col: number; row: number; subC: number; subR: number; stage: number }
  /** A sub-cell just got a seed dropped on it. */
  | { type: 'subCellSeeded'; col: number; row: number; subC: number; subR: number }
  /** A sub-cell just got watered. `stage` 1 damp / 2 moist / 3 soaked. */
  | { type: 'subCellWatered'; col: number; row: number; subC: number; subR: number; stage: number }
  /** A mature crop sub-cell was just cut with the sickle. */
  | { type: 'subCellHarvested'; col: number; row: number; subC: number; subR: number; cropType: string }
  | { type: 'orderPlaced'; cost: number }
  | { type: 'orderDelivered'; lines: SeedOrderLine[] }
  /** Direct seed purchase from the truck panel — money debit + seeds credit.
   *  Distinct from `orderPlaced` (which is the mailbox round-trip flow). */
  | { type: 'seedsBought'; seed: SeedType; qty: number; cost: number }
  /** Seeds the truck fetched from the city on its return trip — they
   *  land in inventory now, distinct from seedsBought (purchase only). */
  | { type: 'seedsDelivered'; seed: SeedType; qty: number }
  | { type: 'hint'; text: string; warn?: boolean }
  | { type: 'blocked'; reason: string }
  | { type: 'stateChanged' }
  /** One axe / pickaxe swing landed but the target hasn't been fully
   *  felled yet (still needs more strikes). Diorama emits this for
   *  intermediate swings only — the FINAL swing produces `chopped`
   *  instead. Sounds picks the right per-tool impact cue based on
   *  `kind`. Single-strike clears (bush, sapling, log, stump) skip
   *  this entirely and go straight to `chopped`. */
  | { type: 'strikeImpact'; col: number; row: number; kind: 'tree' | 'rock' }
  /** A fly-to-HUD glyph just landed on its destination chip. Fired by
   *  FlyToHud at arrival time so Sounds can play a per-item pop and the
   *  chip can bounce. `id` matches the chip's `data-id` (e.g. 'basket',
   *  'mushroom', 'money'). */
  | { type: 'itemLanded'; id: string }
  /** Player clicked a non-building tile with a manual single-tile crop tool
   *  in the overview camera — Diorama tweens the camera to a low-angle
   *  zoom on the 6×6 sector centered on (col, row). */
  | { type: 'zoomEntered'; col: number; row: number; tool: 'field' | 'plant' | 'water' | 'scythe' }
  | { type: 'zoomExited' };

type Handler = (e: SimEvent) => void;

export class EventBus {
  private handlers = new Set<Handler>();
  on(h: Handler) { this.handlers.add(h); return () => this.handlers.delete(h); }
  emit(e: SimEvent) { for (const h of this.handlers) h(e); }
}
